/**
 * TNK-140 phase D.2 — Quick View modal absorbing the Figma's
 * CardMatchLookupModal design. Two-pane layout:
 *
 *   ┌─ Left rail (240px) ──────────┐ ┌─ Right pane ─────────────┐
 *   │ Uploaded thumbnail           │ │  Search box              │
 *   │ Selected candidate thumbnail │ │  Candidate grid          │
 *   │ + confidence + meta block    │ │    each tile carries a   │
 *   │ Score breakdown (expandable) │ │    confidence pill +     │
 *   │                              │ │    selected indicator    │
 *   └──────────────────────────────┘ └──────────────────────────┘
 *   Footer: Cancel · (Replace Card when selection ≠ initial top)
 *
 * Confidence is computed CLIENT-SIDE per candidate against the CSV
 * row — `computeMatchConfidence` already exists and is pure.
 * Clicking a tile just SELECTS; "Replace Card" in the footer is what
 * actually fires the override. Operator can browse without
 * accidentally overwriting.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Check,
  ChevronRight,
  Image as ImageIcon,
  Loader2,
  Search,
  X,
} from "lucide-react";

import { Modal } from "@/components/Modal";
import { FIGMA } from "@/components/import/figmaStyles";
import { imageUrl } from "@/features/imageUpload";
import { confidenceTone, scoreCandidatePool } from "@tcg-web/normalize";
import {
  searchCardCatalog,
  type CatalogCandidate,
} from "@/features/catalogSearch";

export interface QuickViewInitial {
  name?: string;
  setName?: string;
  cardNumber?: string;
  game?: string;
}

export interface QuickViewCsv {
  name?: string;
  setName?: string;
  cardNumber?: string;
}

/**
 * The operator's row data, displayed prominently in the left rail
 * so they can compare against any candidate they preview. Optional
 * fields render only when present.
 */
export interface QuickViewIdentified {
  name?: string;
  setName?: string;
  cardNumber?: string;
  year?: string;
  rarity?: string;
  artist?: string;
  type?: string;
}

/**
 * TNK-141 phase F — the meta-row keys the operator can individually
 * accept (or skip) when applying a catalog candidate. Each key groups
 * the logically-paired underlying parsed fields (e.g. `set` covers
 * both `setName` and `setCode`). The set passed back via `onSelect`
 * tells the parent which groups to copy from the candidate.
 */
export type FieldKey =
  | "name"
  | "set"
  | "cardNumber"
  | "year"
  | "rarity"
  | "type"
  | "artist";

const ALL_FIELD_KEYS: FieldKey[] = [
  "name",
  "set",
  "cardNumber",
  "year",
  "rarity",
  "type",
  "artist",
];

/** Which field groups have a value on the given candidate? Used to
 *  default the accept toggles when the operator picks a candidate. */
function populatedFields(c: CatalogCandidate): Set<FieldKey> {
  const out = new Set<FieldKey>();
  if (c.name) out.add("name");
  if (c.setName || c.setId) out.add("set");
  if (c.number) out.add("cardNumber");
  if (c.releasedAt) out.add("year");
  if (c.rarity) out.add("rarity");
  if (c.supertype || (c.types && c.types.length > 0)) out.add("type");
  if (c.artist) out.add("artist");
  return out;
}

export function QuickViewModal({
  open,
  onClose,
  initial,
  csv,
  uploadedImageRef,
  identified,
  preResolved,
  onSelect,
  sourceLabel = "Imported",
}: {
  open: boolean;
  onClose: () => void;
  initial: QuickViewInitial;
  /** Operator's original CSV values — drive per-candidate confidence. */
  csv: QuickViewCsv;
  /** Image ref of the operator's uploaded photo (item.imageFrontRef). */
  uploadedImageRef: string | null;
  /** The row's currently-identified data — always visible in left rail. */
  identified: QuickViewIdentified;
  /**
   * TNK-141 phase E — server-persisted catalog candidate (from
   * `item.catalog`). When set, the modal opens with this candidate as
   * the default "Identified" selection without waiting for the search
   * to land. The catalog match also defines what "Confirm" means: the
   * action button reads "Confirm card" until the operator picks a
   * different candidate from the grid.
   */
  preResolved?: CatalogCandidate | null;
  /**
   * TNK-141 phase F — operator chose to apply the catalog candidate.
   * `acceptedFields` is the set of meta-row keys (`name`, `set`,
   * `cardNumber`, `year`, `rarity`, `type`, `artist`) the operator
   * left checked in the Identified panel — defaults to all populated
   * fields, but can be narrowed per-row to keep their CSV value for
   * specific fields while accepting the upstream for others. An empty
   * set means "no field changes" — parent should treat as a no-op
   * confirmation (still flips status to fixed via /override).
   */
  onSelect: (
    candidate: CatalogCandidate,
    acceptedFields: Set<FieldKey>,
  ) => void;
  /** Heading for the left-rail "source" comparison card. Defaults to
   *  "Imported" (CSV wizard); the scan review passes "Scanned". */
  sourceLabel?: string;
}) {
  // Pre-fill with just the name — both upstreams choke on extra terms.
  const initialQuery = useMemo(
    () => (initial.name ?? "").trim(),
    [initial.name],
  );

  const [query, setQuery] = useState(initialQuery);
  const [debounced, setDebounced] = useState(initialQuery);
  const [candidates, setCandidates] = useState<CatalogCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showBreakdown, setShowBreakdown] = useState(false);
  // TNK-141 phase F — per-field accept toggles. Operator can uncheck
  // specific catalog fields to keep their CSV value for those while
  // accepting the upstream for the rest.
  const [acceptedFields, setAcceptedFields] = useState<Set<FieldKey>>(
    new Set(),
  );
  const inputRef = useRef<HTMLInputElement>(null);

  // Game stays inferred from the row's parsed shape — operator's
  // CSV is the source of truth. Falls back to Pokemon when missing.
  const game: "Pokemon" | "Magic" =
    initial.game === "Magic" ? "Magic" : "Pokemon";

  // Reset every open. Default the selection to the server's pre-
  // resolved catalog candidate when one is provided so the Identified
  // panel renders something useful before the search returns.
  useEffect(() => {
    if (open) {
      setQuery(initialQuery);
      setDebounced(initialQuery);
      setCandidates([]);
      setError("");
      setSelectedId(preResolved?.id ?? null);
      setShowBreakdown(false);
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
    }
  }, [open, initialQuery, preResolved?.id]);

  // Debounce.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Fetch.
  useEffect(() => {
    if (!open) return;
    if (debounced.trim().length < 2) {
      setCandidates([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    void searchCardCatalog({ q: debounced.trim(), game, limit: 50 })
      .then((r) => {
        if (!cancelled) setCandidates(r.candidates);
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message ?? "Search failed.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, game, open]);

  // Score every candidate as a pool — the helper applies a uniqueness
  // boost only to the unique top match. So a lone perfect match earns
  // 100 even with partial CSV fields, while a wrong-set sibling stays
  // at its base ~50. Sort descending so the top match leads.
  const scored = useMemo(() => {
    const scores = scoreCandidatePool({
      csv: {
        name: csv.name,
        setName: csv.setName,
        cardNumber: csv.cardNumber,
      },
      candidates: candidates.map((c) => ({
        name: c.name,
        setName: c.setName,
        number: c.number,
      })),
    });
    return candidates
      .map((candidate, i) => ({ candidate, score: scores[i] ?? 0 }))
      .sort((a, b) => b.score - a.score);
  }, [candidates, csv.name, csv.setName, csv.cardNumber]);

  // Default-select the top match once results land — but only if the
  // operator doesn't already have a server-resolved candidate locked
  // in. preResolved wins so the modal opens with the right pick.
  useEffect(() => {
    if (selectedId === null && scored.length > 0) {
      setSelectedId(scored[0].candidate.id);
    }
  }, [scored, selectedId]);

  // `selected` falls back to preResolved when the search hasn't
  // landed yet (or the catalog candidate isn't in the search result
  // for whatever reason) so the Identified panel never goes blank.
  const selectedFromScored =
    scored.find((s) => s.candidate.id === selectedId) ?? null;
  const selected =
    selectedFromScored ??
    (preResolved && selectedId === preResolved.id
      ? { candidate: preResolved, score: 100 }
      : null);
  // The "top" the operator is confirming = the server's pre-resolved
  // catalog candidate when present, else the top-scored search result.
  // Determines whether the action button reads "Confirm" or "Accept".
  const topId = preResolved?.id ?? scored[0]?.candidate.id ?? null;
  const isCurrentMatch = selectedId !== null && selectedId === topId;
  const canAct = selectedId !== null;

  // Reset acceptedFields whenever the selection changes — default to
  // accepting every populated catalog field, mirroring the prior
  // "Replace card" full-overwrite behavior. Operator can then untoggle
  // specific fields they want to keep from their CSV.
  useEffect(() => {
    if (selected) {
      setAcceptedFields(populatedFields(selected.candidate));
    } else {
      setAcceptedFields(new Set());
    }
  }, [selected?.candidate.id]);

  const toggleField = (key: FieldKey) => {
    setAcceptedFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const uploadedSrc = uploadedImageRef
    ? imageUrl(uploadedImageRef, "thumb")
    : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      ariaLabel="Quick view — search for the correct card"
      panelClassName="w-[min(1280px,95vw)] max-h-[90vh] flex flex-col overflow-hidden"
      panelStyle={{
        border: `1px solid rgba(99,102,241,0.2)`,
        boxShadow:
          "0 32px 80px rgba(0,0,0,0.85), 0 0 60px rgba(99,102,241,0.10)",
      }}
    >
      {/* Top accent gradient line — pointer-events-none so it never
          intercepts the header's close button click. The panel
          already has fixed positioning from Modal.tsx, which is
          enough to anchor this absolute child. */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px rounded-t-lg"
        style={{
          background:
            "linear-gradient(90deg, transparent, rgba(99,102,241,0.6), transparent)",
        }}
      />

      {/* Header */}
      <div className="flex items-start justify-between gap-3 border-b border-border p-4">
        <div className="flex flex-wrap items-center gap-2">
          <h2
            style={{
              fontFamily: FIGMA.fontHeading,
              fontSize: 15,
              fontWeight: 700,
              color: FIGMA.fg,
            }}
          >
            Quick view
          </h2>
          {identified.name && (
            <span
              className="text-xs"
              style={{ color: FIGMA.muted, fontFamily: FIGMA.fontBody }}
            >
              — {identified.name}
              {identified.setName ? ` · ${identified.setName}` : ""}
              {identified.cardNumber ? ` · #${identified.cardNumber}` : ""}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X size={16} />
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-1 min-h-0 flex-col lg:flex-row">
        {/* ── Comparison rail: Imported | Identified side-by-side ── */}
        <aside className="flex w-full shrink-0 flex-col gap-3 overflow-y-auto border-b border-border bg-muted/20 p-4 sm:flex-row lg:w-[520px] lg:border-b-0 lg:border-r">
          {/* Source — the operator's row data + their photo (CSV import or
              the scan's vision read). */}
          <ComparisonCard
            label={sourceLabel}
            thumbSrc={uploadedSrc}
            thumbAlt="Uploaded photo"
            data={identified}
            accentColor={FIGMA.indigo}
          />

          {/* Identified — the currently-selected candidate from the
              catalog grid. Empty placeholder until the operator picks
              (or the default top-match is auto-selected). TNK-141 F:
              per-field accept toggles let the operator selectively
              merge fields rather than full-overwrite. */}
          <ComparisonCard
            label="Identified"
            thumbSrc={selected?.candidate.imageSmall ?? null}
            thumbAlt="Catalog match"
            data={
              selected
                ? {
                    name: selected.candidate.name,
                    setName: selected.candidate.setName,
                    cardNumber: selected.candidate.number,
                    year: selected.candidate.releasedAt?.slice(0, 4),
                    rarity: selected.candidate.rarity,
                    artist: selected.candidate.artist,
                    type: selected.candidate.supertype,
                  }
                : null
            }
            score={selected?.score}
            emptyMessage="Pick a candidate from the grid →"
            acceptable={selected ? acceptedFields : undefined}
            onToggleField={selected ? toggleField : undefined}
            breakdown={
              selected && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowBreakdown((v) => !v)}
                    className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
                  >
                    Score breakdown
                    <ChevronRight
                      size={10}
                      className={`transition-transform ${showBreakdown ? "rotate-90" : ""}`}
                    />
                  </button>
                  {showBreakdown && (
                    <ScoreBreakdown candidate={selected.candidate} csv={csv} />
                  )}
                </>
              )
            }
          />
        </aside>

        {/* ── Right pane: search + grid ── */}
        <div className="flex flex-1 flex-col">
          {/* Search box */}
          <div className="border-b border-border p-4">
            <div className="relative">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, set, or card number…"
                className="w-full rounded-md border border-border bg-background py-2 pl-9 pr-3 text-sm outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-y-auto p-4">
            {error && (
              <div className="mb-2 rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
                {error}
              </div>
            )}
            {loading ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                <Loader2 size={16} className="animate-spin" />
              </div>
            ) : scored.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-muted-foreground">
                <Search size={20} className="opacity-40" />
                <p className="text-xs">
                  {debounced.trim().length < 2
                    ? "Type at least 2 characters."
                    : "No matches in the catalog."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                {scored.map(({ candidate, score }) => (
                  <CandidateTile
                    key={candidate.id}
                    candidate={candidate}
                    score={score}
                    selected={selectedId === candidate.id}
                    onClick={() => setSelectedId(candidate.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/10 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          className="rounded-xl px-4 py-2 transition-all"
          style={{
            background: "rgba(255,255,255,0.04)",
            border: `1px solid ${FIGMA.panelBorderHover}`,
            color: FIGMA.muted,
            fontFamily: FIGMA.fontBody,
            fontSize: 13,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.08)";
            e.currentTarget.style.color = FIGMA.fg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.04)";
            e.currentTarget.style.color = FIGMA.muted;
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            if (selected) onSelect(selected.candidate, acceptedFields);
          }}
          disabled={!canAct}
          className="inline-flex items-center gap-2 rounded-xl px-5 py-2.5 transition-all disabled:cursor-not-allowed disabled:opacity-50"
          style={{
            background: FIGMA.indigoGradient,
            border: "none",
            color: "#fff",
            fontFamily: FIGMA.fontBody,
            fontSize: 13,
            fontWeight: 600,
            boxShadow: canAct ? FIGMA.indigoShadow : "none",
          }}
          onMouseEnter={(e) => {
            if (canAct)
              e.currentTarget.style.boxShadow = FIGMA.indigoShadowHover;
          }}
          onMouseLeave={(e) => {
            if (canAct) e.currentTarget.style.boxShadow = FIGMA.indigoShadow;
          }}
        >
          <Check size={14} />
          {(() => {
            // Footer label communicates exactly what the click does:
            //   - "Confirm card": confirming the server's resolved
            //     pick AND keeping every default-accepted field on
            //     (no per-field deltas — just records the operator's
            //     confirmation).
            //   - "Accept N changes": some toggles flipped OR a
            //     different candidate selected — partial merge fires.
            const fullAccept =
              selected != null &&
              acceptedFields.size ===
                populatedFields(selected.candidate).size &&
              [...populatedFields(selected.candidate)].every((k) =>
                acceptedFields.has(k),
              );
            if (isCurrentMatch && fullAccept) return "Confirm card";
            const n = acceptedFields.size;
            if (n === 0) return "No changes";
            return `Accept ${n} change${n === 1 ? "" : "s"}`;
          })()}
        </button>
      </div>
    </Modal>
  );
}

/* ──────────────────────────  Components  ────────────────────────────────── */

/**
 * One side of the comparison rail — pairs a photo with a labeled data
 * block (Name + Set/#/Year/Rarity/etc). Used twice: once for the
 * operator's imported row data, once for the currently-selected
 * candidate. When `score` is provided, a confidence pill renders next
 * to the label and the thumbnail gets a tone-colored glow.
 */
/** Maps a meta-row label to its FieldKey, so the ComparisonCard can
 *  drive the per-field accept toggles without the parent needing to
 *  know which label corresponds to which underlying parsed field. */
const ROW_LABEL_TO_FIELD: Record<string, FieldKey> = {
  Set: "set",
  "No.": "cardNumber",
  Year: "year",
  Rarity: "rarity",
  Type: "type",
  Artist: "artist",
};

function ComparisonCard({
  label,
  thumbSrc,
  thumbAlt,
  data,
  score,
  accentColor,
  emptyMessage,
  breakdown,
  acceptable,
  onToggleField,
}: {
  label: string;
  thumbSrc: string | null;
  thumbAlt?: string;
  data: QuickViewIdentified | null;
  score?: number;
  accentColor?: string;
  emptyMessage?: string;
  breakdown?: React.ReactNode;
  /** TNK-141 phase F — when provided, each meta row renders a small
   *  accept checkbox driven by membership in this set. Plus an
   *  Accept toggle on the name row at the top. */
  acceptable?: Set<FieldKey>;
  onToggleField?: (key: FieldKey) => void;
}) {
  const tone = score != null ? confidenceTone(score) : null;
  const glow =
    tone === "high"
      ? FIGMA.good
      : tone === "medium"
        ? FIGMA.warn
        : tone === "low"
          ? FIGMA.bad
          : (accentColor ?? FIGMA.indigo);
  const thumbStyle: React.CSSProperties = {
    aspectRatio: "2.5/3.5",
    border: `1px solid ${glow}40`,
    boxShadow:
      tone != null
        ? `0 0 24px ${glow}33, 0 0 6px ${glow}22`
        : `0 0 12px ${glow}1a`,
  };
  const rows: Array<[string, string | undefined]> = data
    ? [
        ["Set", data.setName],
        ["No.", data.cardNumber ? `#${data.cardNumber}` : undefined],
        ["Year", data.year],
        ["Rarity", data.rarity],
        ["Type", data.type],
        ["Artist", data.artist],
      ]
    : [];
  const filled = rows.filter(([, v]) => !!v);
  return (
    <div
      className="flex flex-1 flex-col gap-2 rounded-lg p-3"
      style={{
        background: "rgba(99,102,241,0.04)",
        border: `1px solid ${FIGMA.indigoBorder}`,
      }}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        {score != null && <ConfidencePill score={score} />}
      </div>
      {thumbSrc ? (
        <img
          src={thumbSrc}
          alt={thumbAlt ?? ""}
          className="w-full rounded object-contain"
          style={thumbStyle}
        />
      ) : (
        <div
          className="flex w-full items-center justify-center rounded border border-dashed border-border text-muted-foreground/40"
          style={{ aspectRatio: "2.5/3.5" }}
        >
          <ImageIcon size={20} />
        </div>
      )}
      {data ? (
        <>
          <div className="flex items-start justify-between gap-2">
            <div
              className="line-clamp-2"
              style={{
                fontFamily: FIGMA.fontHeading,
                fontSize: 13,
                fontWeight: 700,
                color: FIGMA.fg,
                lineHeight: 1.25,
              }}
            >
              {data.name || "—"}
            </div>
            {acceptable && onToggleField && (
              <FieldAcceptToggle
                checked={acceptable.has("name")}
                onChange={() => onToggleField("name")}
              />
            )}
          </div>
          {filled.length > 0 && (
            <div className="space-y-0.5 pt-1">
              {filled.map(([rowLabel, val]) => {
                const fieldKey = ROW_LABEL_TO_FIELD[rowLabel];
                const showToggle =
                  !!acceptable && !!onToggleField && !!fieldKey;
                const accepted = showToggle && acceptable!.has(fieldKey);
                return (
                  <div
                    key={rowLabel}
                    className="flex items-start gap-2 text-[10px]"
                    style={{
                      opacity: showToggle && !accepted ? 0.45 : 1,
                      transition: "opacity 120ms",
                    }}
                  >
                    <span
                      className="shrink-0"
                      style={{ color: FIGMA.dim, minWidth: 36 }}
                    >
                      {rowLabel}
                    </span>
                    <span
                      className="truncate flex-1"
                      style={{
                        color: FIGMA.fgSecondary,
                        fontFamily:
                          rowLabel === "No." || rowLabel === "Year"
                            ? FIGMA.fontMono
                            : FIGMA.fontBody,
                      }}
                    >
                      {val}
                    </span>
                    {showToggle && (
                      <FieldAcceptToggle
                        checked={accepted}
                        onChange={() => onToggleField!(fieldKey)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <p className="text-[11px] text-muted-foreground">
          {emptyMessage ?? "—"}
        </p>
      )}
      {breakdown}
    </div>
  );
}

/**
 * TNK-141 phase F — compact per-field accept toggle. Indigo when
 * checked (operator wants the catalog value), muted/outlined when not
 * (operator keeps the imported value for this field).
 */
function FieldAcceptToggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      role="checkbox"
      aria-checked={checked}
      title={checked ? "Accepting this field" : "Keeping imported value"}
      className="flex h-4 w-4 shrink-0 items-center justify-center rounded transition-all"
      style={{
        background: checked ? FIGMA.indigo : "transparent",
        border: `1px solid ${checked ? FIGMA.indigo : FIGMA.panelBorderHover}`,
      }}
    >
      {checked && <Check size={10} color="#fff" />}
    </button>
  );
}

function ConfidencePill({ score }: { score: number }) {
  const tone = confidenceTone(score);
  const palette =
    tone === "high"
      ? { bg: FIGMA.goodBg, border: FIGMA.goodBorder, fg: FIGMA.good }
      : tone === "medium"
        ? { bg: FIGMA.warnBg, border: FIGMA.warnBorder, fg: FIGMA.warn }
        : { bg: FIGMA.badBg, border: FIGMA.badBorder, fg: FIGMA.bad };
  return (
    <span
      className="inline-flex items-center rounded-md px-1.5 py-0.5"
      style={{
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        color: palette.fg,
        fontFamily: FIGMA.fontMono,
        fontSize: 10,
        fontWeight: 800,
      }}
    >
      {score}%
    </span>
  );
}

/**
 * Per-field score breakdown — shows which dimensions matched the CSV
 * input. Mirrors the Figma's bar-chart look with our weights from
 * @tcg-web/normalize matchConfidence.
 */
function ScoreBreakdown({
  candidate,
  csv,
}: {
  candidate: CatalogCandidate;
  csv: QuickViewCsv;
}) {
  const norm = (s?: string) =>
    s?.trim().toLowerCase().replace(/\s+/g, " ") ?? "";
  const normNum = (s?: string) => {
    if (!s) return "";
    const num = s.split("/")[0].trim();
    return /^\d+$/.test(num) ? String(parseInt(num, 10)) : num.toLowerCase();
  };
  const matches = {
    name: !!csv.name && norm(csv.name) === norm(candidate.name),
    set: !!csv.setName && norm(csv.setName) === norm(candidate.setName),
    number:
      !!csv.cardNumber && normNum(csv.cardNumber) === normNum(candidate.number),
  };
  const rows: Array<{ label: string; weight: number; ok: boolean }> = [
    { label: "Name", weight: 50, ok: matches.name },
    { label: "Set", weight: 30, ok: matches.set },
    { label: "Number", weight: 20, ok: matches.number },
  ];
  const maxWeight = 50;
  return (
    <div className="space-y-1 rounded-md border border-border bg-card/40 p-2 text-[10px]">
      {rows.map(({ label, weight, ok }) => (
        <div key={label} className="flex items-center gap-2">
          <span className="w-12 text-muted-foreground">{label}</span>
          <div className="flex-1 overflow-hidden rounded-full bg-muted">
            <div
              className={`h-1.5 rounded-full ${ok ? "bg-success" : "bg-border"}`}
              style={{ width: `${(weight / maxWeight) * 100}%` }}
            />
          </div>
          <span
            className={`w-6 text-right font-mono ${ok ? "text-success" : "text-muted-foreground"}`}
          >
            {weight}
          </span>
        </div>
      ))}
    </div>
  );
}

function CandidateTile({
  candidate,
  score,
  selected,
  onClick,
}: {
  candidate: CatalogCandidate;
  score: number;
  selected: boolean;
  onClick: () => void;
}) {
  // Selected tiles glow indigo + confidence-tone halo. Subtle but
  // tells the operator at a glance which tile they're committing to.
  const tone = confidenceTone(score);
  const toneColor =
    tone === "high" ? FIGMA.good : tone === "medium" ? FIGMA.warn : FIGMA.bad;
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative flex flex-col gap-1.5 rounded-xl p-2.5 text-left transition-all"
      style={{
        background: selected
          ? "rgba(99,102,241,0.12)"
          : "rgba(255,255,255,0.025)",
        border: `1px solid ${selected ? FIGMA.indigoBorderStrong : FIGMA.panelBorder}`,
        boxShadow: selected
          ? `0 0 24px ${FIGMA.indigo}33, 0 0 8px ${toneColor}33`
          : "none",
      }}
      onMouseEnter={(e) => {
        if (!selected) {
          e.currentTarget.style.background = "rgba(255,255,255,0.04)";
          e.currentTarget.style.borderColor = FIGMA.panelBorderHover;
        }
      }}
      onMouseLeave={(e) => {
        if (!selected) {
          e.currentTarget.style.background = "rgba(255,255,255,0.025)";
          e.currentTarget.style.borderColor = FIGMA.panelBorder;
        }
      }}
    >
      {/* Confidence pill — absolute top-right */}
      <div className="absolute right-1.5 top-1.5">
        <ConfidencePill score={score} />
      </div>

      {/* Selected check — absolute top-left */}
      {selected && (
        <div
          className="absolute left-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full"
          style={{ background: FIGMA.indigo }}
        >
          <Check size={10} color="#fff" />
        </div>
      )}

      {candidate.imageSmall ? (
        <img
          src={candidate.imageSmall}
          alt=""
          className="h-[112px] w-[80px] self-center rounded object-contain"
          style={{
            border: `1px solid ${selected ? FIGMA.indigoBorderStrong : FIGMA.panelBorderHover}`,
          }}
        />
      ) : (
        <div className="flex h-[112px] w-[80px] items-center justify-center self-center rounded border border-dashed border-border text-muted-foreground/40">
          <ImageIcon size={16} />
        </div>
      )}
      <div
        className="line-clamp-1"
        style={{
          fontFamily: FIGMA.fontBody,
          fontSize: 11,
          fontWeight: 600,
          color: FIGMA.fgSecondary,
        }}
      >
        {candidate.name}
      </div>
      <div
        className="line-clamp-1"
        style={{
          fontFamily: FIGMA.fontBody,
          fontSize: 10,
          color: FIGMA.dim,
        }}
      >
        {candidate.setName}
      </div>
      <div className="flex items-center justify-between text-[10px]">
        <span style={{ fontFamily: FIGMA.fontMono, color: FIGMA.veryDim }}>
          #{candidate.number}
        </span>
        {candidate.rarity && (
          <span
            className="rounded px-1 py-0.5"
            style={{
              background: "rgba(255,255,255,0.05)",
              color: FIGMA.dim,
              fontFamily: FIGMA.fontBody,
              fontSize: 9,
            }}
          >
            {candidate.rarity}
          </span>
        )}
      </div>
    </button>
  );
}
